'use client';
import { ContentFields } from '@eliadmin/core/src/components/content-fields/content-fields';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import Image from '@eliadmin/core/src/components/image/image-wrapped';

import { useEffect, useRef, useState } from 'react';

import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useQuery } from '@tanstack/react-query';
import { fetchIconsWithText } from '@eliadmin/core/src/api/general/general-api';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';

const COLLAPSED_HEIGHT_PX = 72;

export function ProductBottomInfo({ inDeal = false }: { inDeal?: boolean }) {
  const { t } = useTranslations();
  const product = useVariantsStore(state => state.product);
  const selectedVariant = useVariantsStore(state => state.selectedVariant);

  const hasVariants = (product?.variants?.length ?? 0) > 0;
  const activeItem = hasVariants ? (selectedVariant ?? product) : product;

  const hasCategories = product.categories && product.categories?.length;
  const descriptionContent =
    activeItem?.description ||
    activeItem?.short_description ||
    product?.description ||
    product?.short_description;

  const availability = activeItem?.availability?.name || product?.availability?.name;
  const productCode = activeItem?.code || product?.code;

  const [isExpanded, setIsExpanded] = useState(false);
  const [isTall, setIsTall] = useState(false);
  const [fullHeight, setFullHeight] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsExpanded(false);
    const el = wrapperRef.current;
    if (!el) return;

    const naturalH = el.scrollHeight;
    setFullHeight(naturalH);
    setIsTall(naturalH > COLLAPSED_HEIGHT_PX);
  }, [descriptionContent]);

  const parameters = [
    {
      name: t('text511'),
      value: product?.width ?? '-',
    },
    {
      name: t('text3688'),
      value: product?.length ?? '-',
    },
    {
      name: t('text512'),
      value: product?.height ?? '-',
    },
    {
      name: t('text339'),
      value: availability,
    },
    {
      name: t('text2481'),
      value: productCode,
    },
  ];

  const locale = useLocaleStore(s => s.currentLocale);

  const { data: icons = [], isLoading } = useQuery({
    queryKey: ['ICONS', locale],
    enabled: !inDeal,
    queryFn: () => fetchIconsWithText({ locale, query: { page: 1, results: 3 } }).then(r => r.data),
  });

  return (
    <div className="flex w-full flex-col items-start justify-start gap-6 sm:gap-8">
      {hasCategories && (
        <div className="flex w-full flex-col items-start justify-start gap-2">
          <Paragraph size="sm" color="neutral" className="font-medium">
            Kategórie:
          </Paragraph>
          <div className="flex w-full flex-wrap items-start justify-start gap-1">
            {product.categories?.map(category => (
              <span
                key={category.name}
                className="flex h-[26px] items-center justify-center whitespace-nowrap rounded-full border px-3 text-xs font-medium text-primary sm:text-sm"
              >
                {category.name}
              </span>
            ))}
          </div>
        </div>
      )}
      {!inDeal && (
        <>
          {descriptionContent && (
            <div className="flex w-full flex-col items-start justify-start gap-2">
              <div
                ref={wrapperRef}
                style={{ maxHeight: isExpanded ? fullHeight + 'px' : COLLAPSED_HEIGHT_PX + 'px' }}
                className="w-full overflow-hidden transition-[max-height] duration-300 ease-in-out"
              >
                <Paragraph
                  size="base"
                  color="neutral"
                  innerHtml={descriptionContent}
                  className={cn('ea-generic-formatting', 'w-full')}
                />
              </div>
              {isTall && (
                <button
                  type="button"
                  onClick={() => setIsExpanded(prev => !prev)}
                  className="text-sm font-medium tracking-tight text-primary"
                >
                  {isExpanded ? t('text528') : t('text3851')}
                </button>
              )}
            </div>
          )}
          <div className="flex w-full flex-col items-start justify-start gap-3 rounded-[20px] border p-4 sm:p-5 lg:p-6">
            <Heading title={t('text9')} tag="h2" className={{ title: 'h4' }} />
            {parameters.map(param => (
              <div key={param.name} className="flex w-full items-center justify-between gap-4">
                <Paragraph size="base" color="neutral" className="text-left">
                  {param.name}:
                </Paragraph>
                <Paragraph size="base" color="black" className="text-right">
                  {param.value}
                </Paragraph>
              </div>
            ))}
          </div>
          {isLoading ? (
            <div className="flex h-[136px] w-full items-center justify-center">
              <Spinner size={'lg'} />
            </div>
          ) : icons.length > 0 ? (
            <div
              className="grid w-full grid-cols-1 gap-[6px] sm:grid-cols-3 md:grid-cols-1
         lg:grid-cols-3 lg:gap-4"
            >
              {icons.map((item, index) => (
                <div
                  key={index}
                  className="flex w-full flex-row items-start justify-start gap-4 rounded-[20px] border p-3 sm:flex-col sm:items-center sm:justify-center"
                >
                  <div className="flex max-h-12 min-h-12 min-w-12 max-w-12 items-center justify-center rounded-[20px] bg-primary">
                    {item.image?.url ? (
                      <Image
                        src={item.image?.url}
                        alt={resolveImageAlt(item?.image, item?.name)}
                        width={24}
                        height={24}
                        className="object-contain object-center"
                      />
                    ) : null}
                  </div>
                  <div className="flex w-full flex-col items-start justify-start gap-1 sm:items-center sm:justify-center">
                    <Heading
                      title={item?.name}
                      tag="h3"
                      className={{ title: 'h5 text-left sm:text-center' }}
                    />
                    <Paragraph
                      size="sm"
                      color="neutral"
                      className="w-full text-left sm:text-center"
                      innerHtml={item?.description}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </>
      )}
      <ContentFields owner={product} module="products" />
    </div>
  );
}
